import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress } from '@material-ui/core';
import helpers, { AddressChoices, AddressKind, IndexedAddress } from './helpers.PassengersAddressesMatrix';
import hooks from './hooks.PassengersAddressesMatrix';
import * as Styles from './styles.PassengersAddressesMatrix';

interface Passenger {
    passId: string;
    fullName: string;
    /** Shown under the name in the dialog */
    departmentName?: string;
}

interface Props {
    passengers: Passenger[];
    choices: AddressChoices;
    onChoicesChange: (choices: AddressChoices) => void;
    /** 'dialog': fills the addresses dialog, with a wider passenger column; 'panel' (default): inside the panel */
    layout?: 'panel' | 'dialog';
    /**
     * The passengers' addresses, when a parent that outlives the matrix fetched them (the picker: the dialog
     * unmounts the matrix on close, which would fetch every passenger again on every opening).
     */
    addressesData?: ReturnType<typeof hooks.usePassengersAddresses>;
}

type MissingByKind = Record<AddressKind, string[]>;

const NO_MISSING: MissingByKind = { pickup: [], drop: [] };

/**
 * Pickup / drop address per passenger when several passengers are placed together.
 * A column per address index: its header applies that index to everybody, its cells to one passenger.
 */
const PassengersAddressesMatrix = ({
    passengers,
    choices,
    onChoicesChange,
    layout = 'panel',
    addressesData,
}: Props) => {
    const inDialog = layout === 'dialog';
    const { t } = useTranslation();

    const passIds = useMemo(() => passengers.map((p) => p.passId), [passengers]);
    // Hooks cannot be conditional: with addresses from the parent, the own fetch gets no passengers and stays idle
    const ownAddressesData = hooks.usePassengersAddresses(addressesData ? [] : passIds);
    const { addressesByPassenger, isLoading, failedPassIds } = addressesData || ownAddressesData;

    // Passengers left on the server default by the last "for all" click, per kind
    const [missing, setMissing] = useState<MissingByKind>(NO_MISSING);

    const columns = useMemo(() => {
        const selectedAddresses: Record<string, IndexedAddress[]> = {};
        passIds.forEach((passId) => {
            selectedAddresses[passId] = addressesByPassenger[passId] || [];
        });
        return helpers.getIndexColumns(selectedAddresses);
    }, [addressesByPassenger, passIds]);

    const isAppliedToAll = (kind: AddressKind, index: number) =>
        helpers.isIndexAppliedToAll(passIds, addressesByPassenger, choices, kind, index);

    const applyToAll = (kind: AddressKind, index: number) => {
        // A second click on the same "for all" undoes it: everybody back to the default
        if (isAppliedToAll(kind, index)) {
            setMissing((prev) => ({ ...prev, [kind]: [] }));
            onChoicesChange(helpers.clearKindForAll(passIds, choices, kind));
            return;
        }
        const result = helpers.applyIndexToAll(passIds, addressesByPassenger, choices, kind, index);
        setMissing((prev) => ({ ...prev, [kind]: result.missingPassIds }));
        onChoicesChange(result.choices);
    };

    const toggle = (passId: string, kind: AddressKind, address: IndexedAddress) => {
        setMissing((prev) => ({ ...prev, [kind]: prev[kind].filter((id) => id !== passId) }));
        onChoicesChange(helpers.togglePassengerChoice(choices, passId, kind, address));
    };

    const failedNames = passengers
        .filter((p) => failedPassIds.includes(p.passId))
        .map((p) => p.fullName)
        .join(', ');
    const hasMissing = missing.pickup.length > 0 || missing.drop.length > 0;

    const renderDialogGrid = () => (
        <Styles.DialogGrid>
            <Styles.DialogRow $columns={columns.length} $isHeader>
                <Styles.DialogNameCell $isHeader>{t('addressesMatrixPassengersList')}</Styles.DialogNameCell>
                {columns.map((index) => (
                    <Styles.DialogHeaderCell key={index}>
                        <span>{t('addressesMatrixIndex', { index })}</span>
                        <Styles.PillsRow data-tour="for-all">
                            <Styles.ForAllPill
                                type="button"
                                $kind="pickup"
                                $pressed={isAppliedToAll('pickup', index)}
                                aria-pressed={isAppliedToAll('pickup', index)}
                                aria-label={t('addressesMatrixPickupForAllAria', { index })}
                                onClick={() => applyToAll('pickup', index)}
                            >
                                {t('addressesMatrixPickupForAll')}
                            </Styles.ForAllPill>
                            <Styles.ForAllPill
                                type="button"
                                $kind="drop"
                                $pressed={isAppliedToAll('drop', index)}
                                aria-pressed={isAppliedToAll('drop', index)}
                                aria-label={t('addressesMatrixDropForAllAria', { index })}
                                onClick={() => applyToAll('drop', index)}
                            >
                                {t('addressesMatrixDropForAll')}
                            </Styles.ForAllPill>
                        </Styles.PillsRow>
                    </Styles.DialogHeaderCell>
                ))}
            </Styles.DialogRow>

            {passengers.map(({ passId, fullName, departmentName }) => {
                const addresses = addressesByPassenger[passId] || [];
                const choice = helpers.getChoice(choices, passId);
                const pickupMissing = missing.pickup.includes(passId);
                const dropMissing = missing.drop.includes(passId);

                return (
                    <Styles.DialogRow
                        key={passId}
                        $columns={columns.length}
                        $isChanged={!!(choice.pickup || choice.drop)}
                        $isWarned={pickupMissing || dropMissing}
                    >
                        <Styles.DialogNameCell>
                            <span>{fullName}</span>
                            <small>{departmentName ? `${passId} · ${departmentName}` : passId}</small>
                            {pickupMissing && <Styles.WarnText>{t('addressesMatrixPickupDefault')}</Styles.WarnText>}
                            {dropMissing && <Styles.WarnText>{t('addressesMatrixDropDefault')}</Styles.WarnText>}
                        </Styles.DialogNameCell>

                        {columns.map((index) => {
                            const address = addresses.find((a) => a.index === index);

                            if (!address) {
                                return (
                                    <Styles.DialogCell key={index}>
                                        <Styles.DialogEmptyCell>
                                            <span aria-hidden="true">—</span>
                                            <Styles.VisuallyHidden>{t('addressesMatrixNoAddress')}</Styles.VisuallyHidden>
                                        </Styles.DialogEmptyCell>
                                    </Styles.DialogCell>
                                );
                            }

                            const isPickup = choice.pickup?.index === index;
                            const isDrop = choice.drop?.index === index;

                            return (
                                <Styles.DialogCell key={index}>
                                    <Styles.DialogAddressText>
                                        {helpers.formatAddress(address)}
                                        {address.isDefault && <span>{` · ${t('addressesMatrixDefaultTag')}`}</span>}
                                    </Styles.DialogAddressText>
                                    <Styles.PillsRow data-tour="pills">
                                        <Styles.Pill
                                            type="button"
                                            $kind="pickup"
                                            $pressed={isPickup}
                                            aria-pressed={isPickup}
                                            aria-label={t('addressesMatrixPickupAria', { index, name: fullName })}
                                            onClick={() => toggle(passId, 'pickup', address)}
                                        >
                                            {t('pickup')}
                                        </Styles.Pill>
                                        <Styles.Pill
                                            type="button"
                                            $kind="drop"
                                            $pressed={isDrop}
                                            aria-pressed={isDrop}
                                            aria-label={t('addressesMatrixDropAria', { index, name: fullName })}
                                            onClick={() => toggle(passId, 'drop', address)}
                                        >
                                            {t('drop')}
                                        </Styles.Pill>
                                    </Styles.PillsRow>
                                </Styles.DialogCell>
                            );
                        })}
                    </Styles.DialogRow>
                );
            })}
        </Styles.DialogGrid>
    );

    const renderBody = () => {
        if (isLoading) {
            return (
                <Styles.Loading>
                    <CircularProgress size={24} aria-label={t('addressesMatrixLoading')} />
                </Styles.Loading>
            );
        }

        if (!columns.length) {
            return <Styles.Note role="status">{t('addressesMatrixEmpty')}</Styles.Note>;
        }

        if (inDialog) return renderDialogGrid();

        return (
            <Styles.Grid>
                <Styles.Row $columns={columns.length} $isHeader $wide={inDialog}>
                    <Styles.NameCell>{t('addressesMatrixPassenger')}</Styles.NameCell>
                    {columns.map((index) => (
                        <Styles.Cell key={index}>
                            <Styles.ColumnTitle>{t('addressesMatrixIndex', { index })}</Styles.ColumnTitle>
                            <Styles.ButtonsRow>
                                <Styles.KindButton
                                    type="button"
                                    $kind="pickup"
                                    aria-label={t('addressesMatrixPickupForAllAria', { index })}
                                    onClick={() => applyToAll('pickup', index)}
                                >
                                    {t('addressesMatrixPickupForAll')}
                                </Styles.KindButton>
                                <Styles.KindButton
                                    type="button"
                                    $kind="drop"
                                    aria-label={t('addressesMatrixDropForAllAria', { index })}
                                    onClick={() => applyToAll('drop', index)}
                                >
                                    {t('addressesMatrixDropForAll')}
                                </Styles.KindButton>
                            </Styles.ButtonsRow>
                        </Styles.Cell>
                    ))}
                </Styles.Row>

                {passengers.map(({ passId, fullName }) => {
                    const addresses = addressesByPassenger[passId] || [];
                    const choice = helpers.getChoice(choices, passId);
                    const pickupMissing = missing.pickup.includes(passId);
                    const dropMissing = missing.drop.includes(passId);

                    return (
                        <Styles.Row
                            key={passId}
                            $columns={columns.length}
                            $isWarned={pickupMissing || dropMissing}
                            $wide={inDialog}
                        >
                            <Styles.NameCell>
                                <strong>{fullName}</strong>
                                <small>{passId}</small>
                                {pickupMissing && <Styles.WarnText>{t('addressesMatrixPickupDefault')}</Styles.WarnText>}
                                {dropMissing && <Styles.WarnText>{t('addressesMatrixDropDefault')}</Styles.WarnText>}
                            </Styles.NameCell>

                            {columns.map((index) => {
                                const address = addresses.find((a) => a.index === index);

                                if (!address) {
                                    return (
                                        <Styles.Cell key={index}>
                                            <Styles.EmptyCell>{t('addressesMatrixNoAddress')}</Styles.EmptyCell>
                                        </Styles.Cell>
                                    );
                                }

                                const isPickup = choice.pickup?.index === index;
                                const isDrop = choice.drop?.index === index;

                                return (
                                    <Styles.Cell key={index}>
                                        <Styles.AddressText>
                                            {helpers.formatAddress(address)}
                                            {address.isDefault && <span>{` · ${t('addressesMatrixDefaultTag')}`}</span>}
                                        </Styles.AddressText>
                                        <Styles.ButtonsRow>
                                            <Styles.KindButton
                                                type="button"
                                                $kind="pickup"
                                                $pressed={isPickup}
                                                aria-pressed={isPickup}
                                                aria-label={t('addressesMatrixPickupAria', { index, name: fullName })}
                                                onClick={() => toggle(passId, 'pickup', address)}
                                            >
                                                {t('pickup')}
                                            </Styles.KindButton>
                                            <Styles.KindButton
                                                type="button"
                                                $kind="drop"
                                                $pressed={isDrop}
                                                aria-pressed={isDrop}
                                                aria-label={t('addressesMatrixDropAria', { index, name: fullName })}
                                                onClick={() => toggle(passId, 'drop', address)}
                                            >
                                                {t('drop')}
                                            </Styles.KindButton>
                                        </Styles.ButtonsRow>
                                    </Styles.Cell>
                                );
                            })}
                        </Styles.Row>
                    );
                })}
            </Styles.Grid>
        );
    };

    const notes = (
        <>
            {hasMissing && <Styles.Note role="status">{t('addressesMatrixMissingNote')}</Styles.Note>}
            {!isLoading && failedNames && (
                <Styles.Note role="alert">{t('addressesMatrixLoadFailed', { names: failedNames })}</Styles.Note>
            )}
        </>
    );

    // The dialog draws its own title, hint and legend around the table
    if (inDialog) {
        return (
            <Styles.DialogSection aria-label={t('addressesMatrixTitle')} data-tour="table">
                {notes}
                {renderBody()}
            </Styles.DialogSection>
        );
    }

    return (
        <Styles.Section aria-labelledby="passengers-addresses-matrix-title">
            <Styles.SectionHeader>
                <Styles.SectionTitle id="passengers-addresses-matrix-title">{t('addressesMatrixTitle')}</Styles.SectionTitle>
                <Styles.Legend>
                    <Styles.LegendItem $kind="pickup">{t('pickup')}</Styles.LegendItem>
                    <Styles.LegendItem $kind="drop">{t('drop')}</Styles.LegendItem>
                </Styles.Legend>
            </Styles.SectionHeader>

            {notes}

            {renderBody()}

            <Styles.Hint>{t('addressesMatrixHint')}</Styles.Hint>
        </Styles.Section>
    );
};

export default PassengersAddressesMatrix;
